import React, { useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './WritePage.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

function WritePage() {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const quillRef = useRef(null);

  const boardNames = {
    'notice': '알림판',
    'sermon': '설교모음',
    'mark': '매일묵상',
    'comment': '설교비평',
    'berg': '판넨베르크 아카데미',
    'current': '세상읽기',
    'free': '사랑채',
    'member': '후원안내',
    'churchs': '교회소식'
  };

  // 이미지 핸들러 (커스텀)
  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('이미지 크기는 5MB 이하여야 합니다.');
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const range = quillRef.current.getEditor().getSelection(true);
          quillRef.current.getEditor().insertEmbed(range.index, 'image', e.target.result);
        };
        reader.onerror = () => {
          alert('이미지 업로드 실패');
        };
        reader.readAsDataURL(file);
      }
    };
  };

  // Quill 모듈 설정 (툴바 등)
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    // Quill 에디터의 내용이 비어있는지 체크 (HTML 태그만 남은 경우 처리)
    const isContentEmpty = content.replace(/<(.|\n)*?>/g, '').trim().length === 0 && !content.includes('<img');
    if (isContentEmpty) {
      setError('내용을 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('로그인이 필요합니다.');
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/post/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          board: boardId,
          title: title.trim(),
          content: content
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.debug) {
          console.error('Debug Info:', data.debug);
          alert(`작성 실패: ${data.debug.message}\n현재 계정 권한: ${data.debug.userAdmin === 'Y' ? '관리자(Y)' : `일반(${data.debug.userAdmin})`}\n게시판: ${data.debug.board}`);
        }
        throw new Error((data.error || '글 작성에 실패했습니다.') + (data.details ? `\n상세: ${data.details}` : ''));
      }

      alert('글이 성공적으로 작성되었습니다.');
      navigate(`/board/${boardId}`);
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.message || '글 작성 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="write-page">
      <div className="write-container">
        <h1>{boardNames[boardId] || '게시판'} - 글쓰기</h1>

        {error && (
          <div className="error-message" style={{
            padding: '12px',
            marginBottom: '20px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="write-form">
          <div className="form-group">
            <label htmlFor="title">제목</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목을 입력하세요"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                marginBottom: '20px'
              }}
            />
          </div>

          <div className="form-group" style={{ height: '500px', marginBottom: '50px' }}>
            <label>내용</label>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={content}
              onChange={setContent}
              modules={modules}
              style={{ height: '400px', backgroundColor: '#fff' }}
              placeholder="내용을 입력하세요..."
            />
          </div>

          <div className="form-actions" style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            marginTop: '24px'
          }}>
            <button
              type="button"
              onClick={() => navigate(`/board/${boardId}`)}
              className="btn-cancel"
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              취소
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#007bff',
                color: 'white',
                cursor: isSubmitting ? 'wait' : 'pointer',
                opacity: isSubmitting ? 0.6 : 1
              }}
            >
              {isSubmitting ? '작성 중...' : '작성하기'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WritePage;
